/**
 * Wallet provider backends.
 *
 * Each chain declares which wallet backend it uses via `ChainConfig.walletProvider`.
 * When omitted, a chain defaults to WEB3REACT (the historical injected / WalletConnect /
 * Coinbase / EIP-6963 stack), so every existing chain keeps its exact current behavior.
 *
 * The concrete adapter for each kind lives in `src/utils/wallet/providers/<kind>/adapter.tsx`
 * and is lazy-loaded so a deployment only bundles the SDK for its active chain.
 *
 * This enum is intentionally a dependency-free leaf: both the chain-config layer
 * (`config/chains`) and the wallet layer (`utils/wallet`) import it, so it must not
 * import from either to avoid a cycle.
 */
export enum WalletProviderKind {
  /** Default: MetaMask / WalletConnect v2 / Coinbase / EIP-6963 via @web3-react/core. */
  WEB3REACT = "web3react",
  /** Privy embedded + external wallets (@privy-io/react-auth). e.g. Monad. */
  PRIVY = "privy",
  /** Para embedded wallets, wagmi under the hood (@getpara/react-sdk). e.g. Arc. */
  PARA = "para",
  /** DogeOS wallet SDK (@dogeos/dogeos-sdk). e.g. DogeOS. */
  DOGEOS = "dogeos",
}

/**
 * Environment variables that MUST be present for a given wallet backend to initialize.
 * `WalletProviderGate` validates these before mounting the SDK provider so a missing key
 * surfaces a clear config error instead of a white-screen crash from inside the SDK.
 */
export const WALLET_PROVIDER_REQUIRED_ENV: Record<WalletProviderKind, string[]> = {
  [WalletProviderKind.WEB3REACT]: [],
  [WalletProviderKind.PRIVY]: ["REACT_APP_PRIVY_APP_ID"],
  [WalletProviderKind.PARA]: ["REACT_APP_PARA_API_KEY"],
  [WalletProviderKind.DOGEOS]: ["REACT_APP_DOGEOS_CLIENT_ID"],
};

/** True when the kind is an SDK backend (anything other than the default web3-react stack). */
export const isSdkWalletProvider = (kind: WalletProviderKind): boolean =>
  kind !== WalletProviderKind.WEB3REACT;
